/**
 * Bicarra Branding Configuration
 * ----------------------------------------------------------------------------
 * Single source of truth for product + endorser identity, colors, fonts,
 * legal copy, and feature lists. Import from anywhere via:
 *   import { BRANDING } from "@/lib/branding.config";
 *
 * Strategy: Endorsed Brand — "Bicarra by Veltrix"
 * Product: Bicarra (AI Chatbot Builder for Malaysian businesses)
 * Endorser: Veltrix Technology Center (UPSI Spin-Off)
 * ----------------------------------------------------------------------------
 */

/**
 * @typedef {Object} BrandingConfig
 * @property {Object} product
 * @property {Object} endorser
 * @property {Object} logo
 * @property {Object} colors
 * @property {Object} fonts
 * @property {Object} contact
 * @property {Object} legal
 * @property {Object} features
 * @property {Object} social
 */

/** @type {BrandingConfig} */
export const BRANDING = {
  product: {
    name: "Bicarra",
    fullName: "Bicarra by Veltrix",
    tagline: "AI that speaks with adab.",
    taglineBM: "AI yang beradab, memahami akhlak dan nilai anda.",
    description:
      "AI built for the Malaysian context — grounded in courtesy, good character, and the values your customers live by.",
    productType: "AI with Adab",
    keywords: [
      "AI chatbot",
      "Malaysian business",
      "adab akhlak AI",
      "BM chatbot",
      "adab RAG AI",
    ],
  },

  endorser: {
    name: "Veltrix",
    short: "Veltrix Technology",
    fullName: "Veltrix Technology Center",
    legalEntity: "Veltrix Technology Centre Sdn Bhd",
    tagline: "by Veltrix",
    longTagline: "A Veltrix Technology Center Product",
    website: "https://www.veltrix.technology",
    parent: "An UPSI Spin-Off Initiative",
    description:
      "AI workforce infrastructure built for the Malaysian context.",
  },

  logo: {
    product: "/brand/bicarra-logo.svg",
    productMark: "/brand/bicarra-icon.svg",
    productWhite: "/brand/bicarra-logo-white.svg",
    endorser: "/brand/veltrix-logo.svg",
    lockup: "/brand/bicarra-by-veltrix.svg",
    lockupHorizontal: "/brand/bicarra-by-veltrix-horizontal.svg",
    favicon: "/brand/bicarra-icon.svg",
    appleTouchIcon: "/brand/bicarra-icon.svg",
    ogImage: "/brand/og-image-bicarra.png",
  },

  colors: {
    // === Bicarra Primary (Royal Blue) — signature identity ===
    primary: "#1E3A8A",
    primaryDeep: "#1E40AF",
    primaryLight: "#3B5BBF",
    primaryGlow: "rgba(30, 58, 138, 0.25)",

    // === Veltrix Foundation (endorsement layers) ===
    navyDeepest: "#060F1F",
    navyDeep: "#0A1628",
    navyMid: "#102849",

    // === Veltrix Gold (premium accent) ===
    gold: "#D4AF37",
    goldBright: "#F5C842",
    goldSoft: "rgba(212, 175, 55, 0.15)",
    goldBorder: "rgba(212, 175, 55, 0.35)",

    // === Shared Neutrals ===
    textPrimary: "#FFFFFF",
    textBody: "#B8C5D6",
    textMuted: "#6B7894",
    silver: "#C0C8D4",
    silverDim: "#8995A8",
    borderLine: "rgba(192, 200, 212, 0.12)",

    // === Functional / Semantic ===
    success: "#10B981",
    warning: "#F59E0B",
    error: "#EF4444",
    info: "#3B82F6",
  },

  fonts: {
    heading: "'Cormorant Garamond', serif",
    body: "'Manrope', sans-serif",
    mono: "'JetBrains Mono', monospace",
  },

  contact: {
    supportEmail: "support@bicarra.com",
    salesEmail: "sales@bicarra.com",
    adminEmail: "admin@bicarra.com",
    veltrixEmail: "hello@veltrix.technology",
  },

  legal: {
    get copyright() {
      return "\u00A9 " + new Date().getFullYear() + " Bicarra. A Veltrix Technology Center product.";
    },
    companyRegistration: "Veltrix Technology Centre Sdn Bhd",
    spinOffNote: "An UPSI Spin-Off Initiative",
    pdpaCompliant: true,
    mydigitalAligned: true,
  },

  features: {
    languages: [
      "Adab",
      "Akhlak",
      "Amanah",
      "Malaysian Context",
    ],
    deploymentTypes: [
      "Website Widget",
      "WhatsApp Business",
      "Telegram",
      "Custom API",
    ],
    industries: [
      "E-commerce",
      "Banking",
      "Healthcare",
      "Education",
      "Government",
      "F&B",
    ],
  },

  social: {
    linkedin: "https://linkedin.com/company/veltrix-technology",
    twitter: "@veltrixtech",
    facebook: "https://facebook.com/veltrixtechnology",
  },
};

export default BRANDING;
